import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { apiFetch } from '../../utils/api';
import '../../styles/admin/EditResources.css';
import ResourceCategories from '../../constants/resourceCategories';
import ProtectedAdminRoute from '../../components/ProtectedAdminRoute';

// Removed BACKEND_URL, use apiFetch instead

export default function EditResources() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [allTitles, setAllTitles] = useState([]); // All resource titles for datalist
  const [results, setResults] = useState([]);
  const [form, setForm] = useState(null);
  const [message, setMessage] = useState('');

  // Fetch all resource titles on mount
  useEffect(() => {
    apiFetch('/api/resources')
      .then(data => setAllTitles((data.resources || []).map(r => r.title)));
  }, []);

  // Fetch resource details as user types (for selection)
  useEffect(() => {
    if (search.length < 1) {
      setResults([]);
      return;
    }
    apiFetch(`/api/resources?title=${encodeURIComponent(search)}`)
      .then(data => {
        setResults(data.resources || []);
      })
      .catch(() => {
        setResults([]);
      });
  }, [search]);

  const handleSelect = async (title) => {
    try {
      const data = await apiFetch(`/api/resources?title=${encodeURIComponent(title)}`);
      // Try exact match first
      const exactMatch = (data.resources || []).find(r => r.title === title);
      // If no exact match, use first result if available
      const resource = exactMatch || (data.resources && data.resources[0]);
      if (resource) {
        setForm(resource);
        setMessage(`Resource "${resource.title}" loaded successfully`);
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage('Error: Resource not found with that title');
      }
    } catch (err) {
      setMessage(`Error: ${err.message}`);
    }
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form) return;
    setMessage('Saving changes...');
    try {
      const data = await apiFetch(`/api/resources/${form._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (data && data.success) {
        setMessage('Resource updated successfully!');
        setTimeout(() => navigate('/resources'), 1500);
      } else {
        setMessage(data && data.error ? data.error : 'Error updating resource: Please try again');
      }
    } catch (err) {
      setMessage(`Connection error: ${err.message}`);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete "${form.title}"? This cannot be undone.`)) return;
    const data = await apiFetch(`/api/resources/${form._id}`, { method: 'DELETE' });
    if (data && data.success) {
      setMessage('Resource deleted!');
      setForm(null);
      setSearch('');
      setTimeout(() => navigate('/resources'), 1000);
    } else {
      setMessage('Error deleting resource');
    }
  };

  return (
    <ProtectedAdminRoute>
      <div className="add-project-flex">
      <div className="edit-project-form">
        <h2>Edit Resource</h2>
        <input
          type="text"
          placeholder="Search resource by title..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          list="resource-titles"
          autoComplete="off"
        />
        <datalist id="resource-titles">
          {allTitles.map(title => (
            <option key={title} value={title} />
          ))}
        </datalist>
        {search && !form && results.length > 0 && (
          <div style={{ marginBottom: '1rem', padding: '10px', backgroundColor: '#f8f9fa', border: '1px solid #ddd', borderRadius: '5px' }}>
            {results.some(r => r.title === search) ? (
              <button
                style={{ padding: '5px 10px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px' }}
                onClick={() => handleSelect(search)}
                type="button"
              >
                Edit: "{search}"
              </button>
            ) : (
              <div>
                <div style={{ fontStyle: 'italic', marginBottom: '5px' }}>Select a resource:</div>
                {results.map(r => (
                  <button
                    key={r._id}
                    style={{ margin: '3px', padding: '5px 10px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px' }}
                    onClick={() => handleSelect(r.title)}
                    type="button"
                  >
                    {r.title}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {form && (
          <div>
            <div style={{ 
              background: "#f8f9fa", 
              padding: "1rem", 
              borderRadius: "0.5rem", 
              marginBottom: "1rem",
              border: "1px solid #e9ecef"
            }}>
              <h4 style={{ margin: "0 0 0.5rem 0", color: "#495057" }}>Resource Statistics</h4>
              <div style={{ display: "flex", gap: "2rem", fontSize: "0.9rem" }}>
                <div>
                  <strong>Click Count:</strong> {form.clickCount || 0} times
                </div>
                <div>
                  <strong>Last Accessed:</strong> {
                    form.lastAccessed 
                      ? new Date(form.lastAccessed).toLocaleDateString() + ' at ' + new Date(form.lastAccessed).toLocaleTimeString()
                      : 'Never'
                  }
                </div>
              </div>
            </div>
            <form onSubmit={handleSubmit} autoComplete="off">
            <input
              name="title"
              placeholder="Resource Title"
              value={form.title}
              onChange={handleChange}
              required
              autoComplete="off"
            />
            <input
              name="link"
              placeholder="Resource URL"
              value={form.link}
              onChange={handleChange}
              required
              type="url"
              pattern="https?://.+"
              autoComplete="off"
            />
            <textarea
              name="description"
              placeholder="Resource description"
              value={form.description}
              onChange={handleChange}
              rows={3}
              autoComplete="off"
            />
            <select
              name="category"
              value={form.category}
              onChange={handleChange}
              required
              className="select-category"
            >
              <option value="" disabled>
                Select Category
              </option>
              {ResourceCategories.OPTIONS.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
            <button type="submit">Save Changes</button>
            <button
              type="button"
              className="remove-resource-btn"
              onClick={handleDelete}
            >
              Remove Resource
            </button>
            </form>
          </div>
        )}
        {message && (
          <div
            className={`message ${message.includes('Error') || message.includes('error') ? 'message-error' : 'message-success'}`}
          >
            {message}
          </div>
        )}
      </div>
    </div>
    </ProtectedAdminRoute>
  );
}